/**
 * Change Decoration Plugin for ProseMirror
 *
 * Renders change decorations for replay visualization. This plugin creates
 * visual highlights for additions and deletions in document replay.
 *
 * Features:
 * - Inline decorations for additions (green for typed, yellow for paste)
 * - Widget decorations for deletions (red strikethrough text)
 * - Position mapping from plain text offsets to ProseMirror positions
 * - Auto-scroll compatible via data-change-range attributes
 *
 * Color scheme matches existing ReplayDocument styling:
 * - paste: bg-amber-100 (yellow)
 * - typed/insert: bg-emerald-100 (green)
 * - delete: bg-red-100 line-through text-red-700 (red strikethrough)
 */

import type { Transaction, EditorState } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { textPosToProseMirrorPos } from "../utils/positionMapping";

/**
 * Type of change for visualization.
 */
export type ChangeType = "addition" | "deletion";

/**
 * Supported event types for replay.
 */
export type ReplayEventType =
  | "insert"
  | "delete"
  | "replace"
  | "paste"
  | "undo"
  | "redo";

/**
 * Represents a change region for visualization in the replay feature.
 * This interface mirrors the one in @markeroo/replay for compatibility.
 */
export interface ChangeRange {
  /** Type of change */
  type: ChangeType;
  /** Start position in the document (plain text offset) */
  from: number;
  /** End position in the document (plain text offset, for additions) */
  to: number;
  /** Deleted text content (for deletions only) */
  deletedText?: string;
  /** Original event type for styling differentiation */
  eventType: ReplayEventType;
}

/**
 * CSS classes for addition highlights based on event type.
 *
 * | Event Type | Background Color |
 * |------------|------------------|
 * | paste      | bg-amber-100 (yellow) |
 * | other      | bg-emerald-100 (green) |
 */
function getAdditionClass(eventType: ReplayEventType): string {
  if (eventType === "paste") {
    // eslint-disable-next-line no-restricted-syntax -- Amber 100 for paste highlight (matches ReplayDocument)
    return "bg-amber-100 rounded-sm px-0.5";
  }
  // eslint-disable-next-line no-restricted-syntax -- Emerald 100 for insert highlight (matches ReplayDocument)
  return "bg-emerald-100 rounded-sm px-0.5";
}

/**
 * CSS classes for deletion highlights.
 */
// eslint-disable-next-line no-restricted-syntax -- Red 100 for deletion highlight (matches ReplayDocument)
const DELETION_CLASS = "bg-red-100 line-through text-red-700 rounded-sm px-0.5";

/**
 * Plugin state interface for tracking change decorations.
 */
interface ChangeDecorationState {
  decorations: DecorationSet;
  changeRanges: ChangeRange[];
}

export const changeDecorationPluginKey = new PluginKey<ChangeDecorationState>(
  "changeDecoration"
);

/**
 * Create decorations from an array of change ranges.
 *
 * @param changeRanges - Array of change ranges to convert to decorations
 * @param doc - The ProseMirror document node for position mapping
 * @returns A DecorationSet containing all change decorations
 */
export function createChangeDecorations(
  changeRanges: ChangeRange[],
  doc: ProseMirrorNode
): DecorationSet {
  const decorations: Decoration[] = [];

  for (const range of changeRanges) {
    if (range.type === "addition") {
      // Convert plain text positions to ProseMirror positions
      const from = textPosToProseMirrorPos(range.from, doc);
      const to = textPosToProseMirrorPos(range.to, doc);

      // Skip invalid ranges
      if (from >= to) continue;

      // Create inline decoration for additions
      decorations.push(
        Decoration.inline(from, to, {
          class: getAdditionClass(range.eventType),
          "data-change-range": "addition",
          "data-event-type": range.eventType,
        })
      );
    } else if (range.type === "deletion" && range.deletedText) {
      // Convert plain text position to ProseMirror position
      const pos = textPosToProseMirrorPos(range.from, doc);

      // Create widget decoration for deletions
      decorations.push(
        Decoration.widget(
          pos,
          () => {
            const span = document.createElement("span");
            span.setAttribute("data-change-range", "deletion");
            span.setAttribute("data-event-type", range.eventType);
            span.className = DELETION_CLASS;
            span.textContent = range.deletedText ?? "";
            return span;
          },
          { side: 0 } // Insert before content at this position
        )
      );
    }
  }

  return DecorationSet.create(doc, decorations);
}

/**
 * Create a change decoration plugin instance.
 *
 * The plugin maintains a DecorationSet based on the provided change ranges.
 * Update the plugin state by dispatching a transaction with the
 * `setChangeRanges` metadata.
 *
 * @param initialRanges - Optional initial change ranges to display
 * @returns A configured ProseMirror plugin
 *
 * @example
 * ```typescript
 * import { createChangeDecorationPlugin, setChangeRanges } from "@markeroo/editor/plugins";
 *
 * // Create plugin with no initial decorations
 * const plugin = createChangeDecorationPlugin();
 *
 * // Update decorations by dispatching a transaction
 * const tr = view.state.tr;
 * setChangeRanges(tr, newChangeRanges);
 * view.dispatch(tr);
 * ```
 */
export function createChangeDecorationPlugin(
  initialRanges: ChangeRange[] = []
): Plugin<ChangeDecorationState> {
  return new Plugin<ChangeDecorationState>({
    key: changeDecorationPluginKey,

    state: {
      init(_, state) {
        return {
          decorations: createChangeDecorations(initialRanges, state.doc),
          changeRanges: initialRanges,
        };
      },

      apply(tr, value, _oldState, newState) {
        // Check for new change ranges in transaction metadata
        const newRanges = tr.getMeta(changeDecorationPluginKey) as
          | ChangeRange[]
          | undefined;

        if (newRanges !== undefined) {
          // New ranges provided - rebuild decorations
          return {
            decorations: createChangeDecorations(newRanges, newState.doc),
            changeRanges: newRanges,
          };
        }

        if (tr.docChanged) {
          // Document changed - map existing decorations
          return {
            decorations: value.decorations.map(tr.mapping, newState.doc),
            changeRanges: value.changeRanges,
          };
        }

        // No changes - return existing state
        return value;
      },
    },

    props: {
      decorations(state) {
        const pluginState = this.getState(state);
        return pluginState?.decorations ?? DecorationSet.empty;
      },
    },
  });
}

/**
 * Set change ranges on a transaction.
 *
 * Use this helper to update the change decorations by setting
 * transaction metadata that the plugin will read.
 *
 * @param tr - The transaction to modify
 * @param changeRanges - The new change ranges to display
 *
 * @example
 * ```typescript
 * const tr = view.state.tr;
 * setChangeRanges(tr, [
 *   { type: 'addition', from: 0, to: 10, eventType: 'paste' },
 *   { type: 'deletion', from: 10, to: 10, deletedText: 'removed', eventType: 'delete' }
 * ]);
 * view.dispatch(tr);
 * ```
 */
export function setChangeRanges(
  tr: Transaction,
  changeRanges: ChangeRange[]
): void {
  tr.setMeta(changeDecorationPluginKey, changeRanges);
}

/**
 * Get the current change ranges from editor state.
 *
 * @param state - The editor state to read from
 * @returns The current change ranges, or an empty array if none
 */
export function getChangeRanges(state: EditorState): ChangeRange[] {
  const pluginState = changeDecorationPluginKey.getState(state);
  return pluginState?.changeRanges ?? [];
}
